import { useState } from 'react';
import { Button } from '@/shared/ui/button';
import { LeadForm } from '../forms/LeadForm';
import { StakeholderStep } from './StakeholderStep';
import { CaptureMethodStep } from './CaptureMethodStep';
import { ImageCaptureStep } from './ImageCaptureStep';
import { useCreateLead, useExtractFromImage, useStakeholderTypes } from '../hooks';
import { uploadLeadCardImage } from '../api/api';
import type {
  CaptureMethod,
  CreateLeadInput,
  ExtractedLeadFields,
  StakeholderType,
} from '../types';

type WizardStep = 'stakeholder' | 'capture_method' | 'image' | 'form';

interface LeadCaptureWizardProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function LeadCaptureWizard({ onSuccess, onCancel }: LeadCaptureWizardProps) {
  const [step, setStep] = useState<WizardStep>('stakeholder');
  const [stakeholderTypeId, setStakeholderTypeId] = useState<number | null>(null);
  const [captureMethod, setCaptureMethod] = useState<CaptureMethod | null>(null);
  const [extractedFields, setExtractedFields] = useState<Partial<ExtractedLeadFields> | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const { data: stakeholderTypes = [], isLoading: isLoadingTypes } = useStakeholderTypes();
  const createLead = useCreateLead();
  const extractFromImage = useExtractFromImage();

  const selectedType: StakeholderType | undefined = stakeholderTypes.find(
    (t) => t.id === stakeholderTypeId
  );

  const goToCaptureMethod = () => {
    if (stakeholderTypeId != null) setStep('capture_method');
  };

  const goToImageOrForm = () => {
    if (!captureMethod) return;
    if (captureMethod === 'manual_entry') {
      setExtractedFields(null);
      setStep('form');
    } else {
      setPendingFiles([]);
      setExtractedFields(null);
      setStep('image');
    }
  };

  const handleImages = async (files: File[]) => {
    if (files.length === 0) return;
    setPendingFiles(files);
    const file = files[0];
    try {
      const result = await extractFromImage.mutateAsync(file);
      setExtractedFields(result);
      setStep('form');
    } catch {
      setExtractedFields({});
      setStep('form');
    }
  };

  const handleFormSubmit = async (input: CreateLeadInput) => {
    const lead = await createLead.mutateAsync(input);
    const currentFile = pendingFiles[0];
    if (currentFile && lead?.id) {
      try {
        await uploadLeadCardImage(String(lead.id), currentFile);
      } catch {
        // Non-blocking: lead is saved; card image upload can be retried later if needed
      }
    }
    const remaining = pendingFiles.slice(1);
    if (remaining.length > 0) {
      setPendingFiles(remaining);
      const next = remaining[0];
      try {
        const result = await extractFromImage.mutateAsync(next);
        setExtractedFields(result);
      } catch {
        setExtractedFields({});
      }
      setStep('form');
    } else {
      setStep('stakeholder');
      setStakeholderTypeId(null);
      setCaptureMethod(null);
      setExtractedFields(null);
      setPendingFiles([]);
      onSuccess?.();
    }
  };

  const handleBack = () => {
    if (step === 'capture_method') setStep('stakeholder');
    else if (step === 'image') setStep('capture_method');
    else if (step === 'form') {
      if (captureMethod === 'manual_entry') {
        setStep('capture_method');
      } else {
        setStep('image');
        setExtractedFields(null);
        if (pendingFiles.length <= 1) setPendingFiles([]);
      }
    }
  };

  return (
    <div className="space-y-6">
      {step === 'stakeholder' && (
        <>
          <StakeholderStep
            types={stakeholderTypes}
            value={stakeholderTypeId}
            onChange={setStakeholderTypeId}
            isLoading={isLoadingTypes}
          />
          <div className="flex gap-2">
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button onClick={goToCaptureMethod} disabled={stakeholderTypeId == null}>
              Next
            </Button>
          </div>
        </>
      )}

      {step === 'capture_method' && (
        <>
          <CaptureMethodStep value={captureMethod} onChange={setCaptureMethod} />
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={handleBack}>
              Back
            </Button>
            <Button onClick={goToImageOrForm} disabled={!captureMethod}>
              Next
            </Button>
          </div>
        </>
      )}

      {step === 'image' && captureMethod && captureMethod !== 'manual_entry' && (
        <>
          <ImageCaptureStep
            captureMethod={captureMethod}
            onImages={handleImages}
            isExtracting={extractFromImage.isPending}
          />
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={handleBack}>
              Back
            </Button>
          </div>
        </>
      )}

      {step === 'form' && selectedType && captureMethod && (
        <>
          <LeadForm
            stakeholderTypeId={selectedType.id}
            stakeholderTypeLabel={selectedType.label}
            captureMethod={captureMethod}
            initialValues={extractedFields ?? undefined}
            onSubmit={handleFormSubmit}
            isSubmitting={createLead.isPending}
            submitLabel={pendingFiles.length > 1 ? 'Save & next card' : 'Save lead'}
          />
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={handleBack}>
              Back
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
